/**
 * Helpers canônicos de data/hora (fuso local, formatos pt-BR).
 * Consolidam os `pad`/`formatDate*`/`formatDuration` que viviam duplicados
 * pelas páginas — use estes em vez de redefinir localmente.
 *
 * Datas de registro migradas do HiDoctor têm regra própria de fuso — ver
 * `utils/recordDate.ts`. Formatos com timezone explícita (America/Sao_Paulo),
 * por extenso ou com dia da semana continuam locais aos seus usos.
 */

/** Zero-pad a 2 dígitos: 7 → "07". */
export const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Date → 'YYYY-MM-DD' (fuso local). Mesmo formato de `input[type=date]` e `Appointment.date`. */
export const dateToYMD = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Epoch ms → 'YYYY-MM-DD' (fuso local). */
export const msToYMD = (ms: number): string => dateToYMD(new Date(ms));

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' (string pura, sem Date — imune a fuso). */
export function ymdToBR(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  if (!y || !m || !d) return ymd;
  return `${d}/${m}/${y}`;
}

/** Epoch ms → 'DD/MM/YYYY' (fuso local); `fallback` quando ms é falsy. */
export function formatDateBR(ms: number | undefined, fallback = '—'): string {
  if (!ms) return fallback;
  const d = new Date(ms);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Epoch ms → 'HH:MM' (fuso local); `fallback` quando ts é falsy. */
export function formatTimeHM(ts: number | undefined, fallback = '--:--'): string {
  if (!ts) return fallback;
  const d = new Date(ts);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Duração em segundos → 'HH:MM:SS' (zero-pad; clampa negativo pra 0). */
export function formatDurationHMS(totalSec: number): string {
  const safe = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

/** Duração em segundos → 'MM:SS' (minutos passam de 59 se preciso: 4500 → "75:00"). */
export function formatDurationMS(totalSec: number): string {
  const safe = Math.max(0, Math.floor(totalSec));
  return `${pad2(Math.floor(safe / 60))}:${pad2(safe % 60)}`;
}

/** Timestamp compacto pra nome de arquivo: 'YYYYMMDD-HHMM'. */
export function fileTimestamp(now: Date = new Date()): string {
  return (
    `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}` +
    `-${pad2(now.getHours())}${pad2(now.getMinutes())}`
  );
}
